import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const http = axios.create({ baseURL: API });

http.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('pbb_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Public browsing: 401s from engage-actions (bid, redeem, purchase, RSVP)
// dispatch a global "pbb:auth-required" event so a modal can prompt the user
// to register/log in WITHOUT navigating them away from the page.
http.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const token = localStorage.getItem('pbb_token');
    if (status === 401 && !token) {
      const url = err?.config?.url || '';
      const isSilent = /\/(auth\/me|config\/public|platform-loyalty|reviews\/can-review|pbb-loyalty\/me|notifications)/.test(url);
      if (!isSilent) {
        window.dispatchEvent(new CustomEvent('pbb:auth-required', { detail: { url } }));
      }
    }
    return Promise.reject(err);
  },
);

export const auth = {
  signup: (data) => http.post('/auth/signup', data).then(r => r.data),
  login: (email, password) => http.post('/auth/login', { email, password }).then(r => r.data),
  me: () => http.get('/auth/me').then(r => r.data),
  forgotPassword: (email) => http.post('/auth/forgot-password', { email }).then(r => r.data),
  resetPassword: (token, new_password) => http.post('/auth/reset-password', { token, new_password }).then(r => r.data),
};

export const businesses = {
  list: (params) => http.get('/businesses', { params }).then(r => r.data),
  get: (id) => http.get(`/businesses/${id}`).then(r => r.data),
  redeem: (id) => http.post(`/businesses/${id}/redeem`).then(r => r.data),
};

export const auction = {
  list: () => http.get('/auction').then(r => r.data),
  subscribe: () => http.post('/auction/subscribe').then(r => r.data),
  bid: (id, amount = null) => http.post(`/auction/${id}/bid`, amount ? { amount } : {}).then(r => r.data),
  end: (id) => http.post(`/auction/${id}/end`).then(r => r.data),
  myBids: () => http.get('/my-bids').then(r => r.data),
  flag: (id, reason, detail = '') => http.post(`/auction/${id}/flag`, { reason, detail }).then(r => r.data),
  confirmCode: (id, code) => http.post(`/auction/${id}/confirm`, { code }).then(r => r.data),
  silentBid: (id, amount) => http.post(`/auction/${id}/silent-bid`, { amount }).then(r => r.data),
  silentStatus: (id) => http.get(`/auction/${id}/silent-status`).then(r => r.data),
  deliveryCreateOrder: (id) => http.post(`/auction/${id}/delivery/create-order`).then(r => r.data),
  deliveryCapture: (id, order_id) => http.post(`/auction/${id}/delivery/capture`, { order_id }).then(r => r.data),
  // Cluster B — Stripe winner checkout
  checkoutState: (id) => http.get(`/auction/${id}/checkout`).then(r => r.data),
  myWins: () => http.get('/my/auction-wins').then(r => r.data),
  checkoutSession: (id) => http.post(`/auction/${id}/checkout/session`, { origin_url: window.location.origin }).then(r => r.data),
  checkoutVerify: (id, session_id) => http.post(`/auction/${id}/checkout/verify`, { session_id }).then(r => r.data),
};

export const donate = {
  submit: (data) => http.post('/donate/listing', data).then(r => r.data),
  mine: () => http.get('/donate/mine').then(r => r.data),
};

export const rules = {
  status: () => http.get('/rules/status').then(r => r.data),
  accept: () => http.post('/rules/accept', { version: 'v1' }).then(r => r.data),
};

export const dealbin = {
  list: () => http.get('/dealbin').then(r => r.data),
  createOrder: (id) => http.post(`/dealbin/${id}/create-order`).then(r => r.data),
  capture: (id, order_id) => http.post(`/dealbin/${id}/capture`, { order_id }).then(r => r.data),
};

export const loyaltyPrograms = {
  directory: (category = null) => http.get('/loyalty-programs', { params: category ? { category } : {} }).then(r => r.data),
  forBusiness: (bid) => http.get(`/businesses/${bid}/loyalty`).then(r => r.data),
  // App-wide PBB Loyalty Card
  pbbMe: () => http.get('/pbb-loyalty/me').then(r => r.data),
  // Per-business QR stamp cards
  checkin: (bid) => http.post(`/loyalty/checkin/${bid}`).then(r => r.data),
  myCards: () => http.get('/loyalty/my-cards').then(r => r.data),
  redeemCard: (cardId) => http.post(`/loyalty/redeem-card/${cardId}`).then(r => r.data),
  ownerQr: (bid) => http.get(`/my-business/${bid}/loyalty-qr`).then(r => r.data),
};

export const funds = {
  live: () => http.get('/funds/live').then(r => r.data),
  recent: (limit = 8) => http.get('/funds/recent', { params: { limit } }).then(r => r.data),
};

export const moderation = {
  queue: () => http.get('/admin/moderation').then(r => r.data),
  approve: (id) => http.post(`/admin/moderation/${id}/approve`).then(r => r.data),
  reject: (id, reason) => http.post(`/admin/moderation/${id}/reject`, { reason }).then(r => r.data),
  overridePricing: (id, startingPrice = null, dealBinPrice = null) =>
    http.post(`/admin/pricing/${id}`, { startingPrice, dealBinPrice }).then(r => r.data),
  overrideDelivery: (id, deliveryFee) =>
    http.post(`/admin/pricing/${id}`, { deliveryFee }).then(r => r.data),
};

export const store = {
  products: () => http.get('/products').then(r => r.data),
  order: (items) => http.post('/orders', { items }).then(r => r.data),
};

export const events = {
  list: () => http.get('/events').then(r => r.data),
  rsvp: (id) => http.post(`/events/${id}/rsvp`).then(r => r.data),
  reserve: (id, quantity = 1, attendeeNames = null) => http.post(`/events/${id}/reserve`, { quantity, attendeeNames }).then(r => r.data),
  checkoutSession: (id, originUrl, quantity = 1, attendeeNames = null) => http.post(`/events/${id}/checkout/session`, { origin_url: originUrl, quantity, attendeeNames }).then(r => r.data),
  verifyCheckout: (id, sessionId) => http.post(`/events/${id}/checkout/verify`, { session_id: sessionId }).then(r => r.data),
  myTickets: () => http.get('/me/tickets').then(r => r.data),
  ticket: (tid) => http.get(`/tickets/${tid}`).then(r => r.data),
  qrUrl: (tid) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}/api/tickets/${tid}/qr.png`;
  },
  hostAttendees: (eid) => http.get(`/events/${eid}/host/attendees`).then(r => r.data),
  hostCheckin: (eid, ticketId) => http.post(`/events/${eid}/host/checkin`, { ticketId }).then(r => r.data),
  cancelTicket: (tid) => http.post(`/tickets/${tid}/cancel`).then(r => r.data),
  listResale: (tid, donateTo = null) => http.post(`/tickets/${tid}/list-resale`, { donateTo }).then(r => r.data),
  withdrawResale: (tid) => http.post(`/tickets/${tid}/withdraw-resale`).then(r => r.data),
  resaleListings: (eid) => http.get(`/events/${eid}/resale`).then(r => r.data),
  resaleMarketplace: () => http.get('/resale/marketplace').then(r => r.data),
  resaleListing: (eid, listingId) => http.get(`/events/${eid}/resale/${listingId}`).then(r => r.data),
  buyResale: (eid, listingId, originUrl) => http.post(`/events/${eid}/resale/${listingId}/checkout`, { origin_url: originUrl }).then(r => r.data),
  verifyResale: (eid, listingId, sessionId) => http.post(`/events/${eid}/resale/${listingId}/verify`, { session_id: sessionId }).then(r => r.data),
  waitlistStatus: (eid) => http.get(`/events/${eid}/waitlist/status`).then(r => r.data),
  waitlistLeave: (eid) => http.post(`/events/${eid}/waitlist/leave`).then(r => r.data),
  myWaitlist: () => http.get('/me/waitlist').then(r => r.data),
};

export const community = {
  posts: () => http.get('/community/posts').then(r => r.data),
  create: (text, image = null) => http.post('/community/posts', { text, image }).then(r => r.data),
  like: (id) => http.post(`/community/posts/${id}/like`).then(r => r.data),
  comments: (id) => http.get(`/community/posts/${id}/comments`).then(r => r.data),
  comment: (id, text) => http.post(`/community/posts/${id}/comments`, { text }).then(r => r.data),
  alertPrefs: () => http.get('/community/alert-prefs').then(r => r.data),
  setAlertPrefs: (commentAlerts) => http.put('/community/alert-prefs', { commentAlerts }).then(r => r.data),
  reportReasons: () => http.get('/community/report-reasons').then(r => r.data),
  report: (payload) => http.post('/community/reports', payload).then(r => r.data),
  adminReports: (status = 'open') => http.get('/admin/community-reports', { params: { status } }).then(r => r.data),
  adminResolve: (id) => http.post(`/admin/community-reports/${id}/resolve`).then(r => r.data),
};

export const blocksApi = {
  list: () => http.get('/blocks').then(r => r.data),
  block: (id) => http.post(`/blocks/${id}`).then(r => r.data),
  unblock: (id) => http.delete(`/blocks/${id}`).then(r => r.data),
};

export const messagesApi = {
  send: (toUserId, text, postId = null) => http.post('/messages', { toUserId, text, postId }).then(r => r.data),
  threads: () => http.get('/messages/threads').then(r => r.data),
  withUser: (id) => http.get(`/messages/with/${id}`).then(r => r.data),
  unread: () => http.get('/messages/unread-count').then(r => r.data),
  emailPrefs: () => http.get('/messages/email-prefs').then(r => r.data),
  setEmailPrefs: (prefs) => http.put('/messages/email-prefs', prefs).then(r => r.data),
};

export const dating = {
  // Legacy demo endpoint (admin-seeded profiles). Kept only to avoid breaking
  // any callers that still hit it.
  profiles: () => http.get('/dating/profiles').then(r => r.data),
  // Real dating portal
  me: () => http.get('/dating/me').then(r => r.data),
  updateMe: (payload) => http.post('/dating/me', payload).then(r => r.data),
  updateSettings: (payload) => http.patch('/dating/me/settings', payload).then(r => r.data),
  browse: () => http.get('/dating/browse').then(r => r.data),
  swipe: (profileUserId, like) => http.post('/dating/swipe', { profileUserId, like }).then(r => r.data),
  myLikes: () => http.get('/dating/likes').then(r => r.data),
  myMatches: () => http.get('/dating/matches').then(r => r.data),
  listMessages: (matchId) => http.get(`/dating/matches/${matchId}/messages`).then(r => r.data),
  sendMessage: (matchId, body) => http.post(`/dating/matches/${matchId}/messages`, { body }).then(r => r.data),
  block: (userId) => http.post('/dating/block', { userId }).then(r => r.data),
  unblock: (userId) => http.post('/dating/unblock', { userId }).then(r => r.data),
  listBlocks: () => http.get('/dating/blocks').then(r => r.data),
  report: (userId, reason) => http.post('/dating/report', { userId, reason }).then(r => r.data),
  submitVerification: (selfie, note = '') => http.post('/dating/verify', { selfie, note }).then(r => r.data),
};

export const misc = {
  leaderboard: () => http.get('/leaderboard').then(r => r.data),
  funds: () => http.get('/funds').then(r => r.data),
  rewards: () => http.get('/rewards').then(r => r.data),
  redeemReward: (id) => http.post(`/rewards/${id}/redeem`).then(r => r.data),
  news: () => http.get('/news').then(r => r.data),
  newsFeed: () => http.get('/news/feed').then(r => r.data),
  contact: (payload) => http.post('/contact', payload).then(r => r.data),
  topReaders: () => http.get('/leaderboard/readers').then(r => r.data),
  setStreakSms: (enabled) => http.put('/news/streak-reminders', { enabled }).then(r => r.data),
  newsMyReads: () => http.get('/news/my-reads').then(r => r.data),
  newsQuiz: (aid) => http.post(`/news/articles/${aid}/quiz`).then(r => r.data),
  newsAnswer: (aid, choice) => http.post(`/news/articles/${aid}/answer`, { choice }).then(r => r.data),
  businessStats: () => http.get('/business-stats').then(r => r.data),
};

export const files = {
  upload: (file, kind = 'post') => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post(`/upload?kind=${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  // Turn a stored path/url into a fully qualified URL usable in <img src>
  urlFor: (u) => {
    if (!u) return u;
    if (u.startsWith('http')) return u;
    if (u.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
    return u;
  },
  // Convert an uploaded-image URL to a specific WebP variant by convention.
  // Non-upload URLs (external CDN, seed data) are passed through unchanged.
  variantUrl: (u, size = 400) => {
    if (!u || !u.startsWith('/api/files/')) return u;
    const m = u.match(/^(.+)\.(png|jpe?g|gif|webp)$/i);
    if (!m) return u;
    return `${m[1]}_${size}.webp`;
  },
  // Fetch { lqip, url_400, url_800 } for a stored image URL — used by legacy
  // uploads that were made before variant-shipping. Generates + caches on
  // the server the first time it's called for a given image.
  lqipFor: (url) => http.get('/files/lqip', { params: { url } }).then(r => r.data),
};

export const owner = {
  myBusinesses: () => http.get('/my-business').then(r => r.data),
  createBusiness: (data) => http.post('/my-business', data).then(r => r.data),
  updateBusiness: (id, data) => http.put(`/my-business/${id}`, data).then(r => r.data),
  deleteBusiness: (id) => http.delete(`/my-business/${id}`).then(r => r.data),
  upgradeBusiness: (id, tier) => http.post(`/my-business/${id}/upgrade`, { tier }).then(r => r.data),
  planTiers: () => http.get('/plan-tiers').then(r => r.data),
  packages: () => http.get('/packages').then(r => r.data),
  myAuctions: () => http.get('/my-auctions').then(r => r.data),
  createAuction: (data) => http.post('/my-auctions', data).then(r => r.data),
  deleteAuction: (id) => http.delete(`/my-auctions/${id}`).then(r => r.data),
  myPayments: () => http.get('/my-payments').then(r => r.data),
  // C.A.N. tier gating — dashboard reads this once to know allowed features + limits
  myTier: () => http.get('/my-tier').then(r => r.data),

  // Per-business coupons
  listCoupons: (bid) => http.get(`/my-business/${bid}/coupons`).then(r => r.data),
  addCoupon: (bid, data) => http.post(`/my-business/${bid}/coupons`, data).then(r => r.data),
  updateCoupon: (bid, cid, data) => http.put(`/my-business/${bid}/coupons/${cid}`, data).then(r => r.data),
  deleteCoupon: (bid, cid) => http.delete(`/my-business/${bid}/coupons/${cid}`).then(r => r.data),

  // Per-business loyalty
  listLoyalty: (bid) => http.get(`/my-business/${bid}/loyalty`).then(r => r.data),
  addLoyalty: (bid, data) => http.post(`/my-business/${bid}/loyalty`, data).then(r => r.data),
  updateLoyalty: (bid, lid, data) => http.put(`/my-business/${bid}/loyalty/${lid}`, data).then(r => r.data),
  deleteLoyalty: (bid, lid) => http.delete(`/my-business/${bid}/loyalty/${lid}`).then(r => r.data),

  // Per-business store items
  listItems: (bid) => http.get(`/my-business/${bid}/store-items`).then(r => r.data),
  addItem: (bid, data) => http.post(`/my-business/${bid}/store-items`, data).then(r => r.data),
  deleteItem: (bid, sid) => http.delete(`/my-business/${bid}/store-items/${sid}`).then(r => r.data),

  // Owner-hosted events (auto-publish with admin review flag)
  myEvents: () => http.get('/my-events').then(r => r.data),
  createEvent: (data) => http.post('/my-events', data).then(r => r.data),
  updateEvent: (eid, data) => http.patch(`/my-events/${eid}`, data).then(r => r.data),
  deleteEvent: (eid) => http.delete(`/my-events/${eid}`).then(r => r.data),
};

export const paypal = {
  createOrder: (pkgId, businessId = null) => http.post('/paypal/create-order', { package: pkgId, business_id: businessId }).then(r => r.data),
  capture: (orderId, pkgId, businessId = null) => http.post('/paypal/capture', { order_id: orderId, package: pkgId, business_id: businessId }).then(r => r.data),
};

export const engagement = {
  track: (business_id, kind, extra = {}) => http.post('/engagement/track', { business_id, kind, ...extra }).then(r => r.data),
  listReviews: (bid) => http.get(`/reviews/${bid}`).then(r => r.data),
  myRating: (bid) => http.get(`/my-rating/${bid}`).then(r => r.data),
  bizStats: (bid) => http.get(`/my-business/${bid}/engagement`).then(r => r.data),
  notifications: () => http.get('/notifications').then(r => r.data),
  readNotif: (id) => http.post(`/notifications/${id}/read`).then(r => r.data),
  // Verified reviews (Phase 3)
  canReview: (bid) => http.get(`/reviews/can-review/${bid}`).then(r => r.data),
  reviewCheckin: (bid, lat, lng) => http.post(`/reviews/checkin/${bid}`, { lat, lng }).then(r => r.data),
  inviteReview: (bid, email, customerName) => http.post(`/business/${bid}/invite-review`, { email, customerName }).then(r => r.data),
  redeemInvite: (token) => http.post(`/reviews/redeem-invite/${token}`).then(r => r.data),
};

export const admin = {
  frozenUsers: () => http.get('/admin/frozen-users').then(r => r.data),
  unfreeze: (uid) => http.post(`/admin/users/${uid}/unfreeze`).then(r => r.data),
  extendFreeze: (uid, days) => http.post(`/admin/users/${uid}/extend-freeze`, { days }).then(r => r.data),
  // C.A.N. Cluster H — manual tier upgrades
  businesses: (q = '') => http.get('/admin/businesses', { params: q ? { q } : {} }).then(r => r.data),
  createBusinessForOwner: (ownerUserId, payload) =>
    http.post(`/admin/businesses/create-for-owner/${ownerUserId}`, payload).then(r => r.data),
  // DELETE /my-business/{bid} — the endpoint has an admin bypass so admins
  // can delete any listing (owner check falls through when isAdmin).
  deleteBusiness: (id) => http.delete(`/my-business/${id}`).then(r => r.data),
  grantPackage: (business_id, packageKey, months = null) =>
    http.post('/admin/grant-package', { business_id, package: packageKey, months }).then(r => r.data),
  packages: () => http.get('/packages').then(r => r.data),
  // Transfer ownership
  userLookup: (q) => http.get('/admin/user-lookup', { params: { q } }).then(r => r.data),
  transferBusiness: (business_id, payload) =>
    http.post(`/admin/businesses/${business_id}/transfer`, { business_id, ...payload }).then(r => r.data),
  // Auction management
  auctions: (params = {}) => http.get('/admin/auctions', { params }).then(r => r.data),
  auctionDetail: (aid) => http.get(`/admin/auctions/${aid}`).then(r => r.data),
  editAuction: (aid, patch) => http.put(`/admin/auctions/${aid}`, patch).then(r => r.data),
  deleteAuction: (aid, reason) => http.delete(`/admin/auctions/${aid}`, { params: reason ? { reason } : {} }).then(r => r.data),
  closeAuction: (aid) => http.post(`/admin/auctions/${aid}/close`).then(r => r.data),
  extendAuction: (aid, hours) => http.post(`/admin/auctions/${aid}/extend`, { hours }).then(r => r.data),
  moveToDealBin: (aid) => http.post(`/admin/auctions/${aid}/move-to-dealbin`).then(r => r.data),
  // Slider Signup Forms (Tier-3 "Send me deals" opt-in)
  listSliderForms: (status) => http.get(`/admin/slider-signup-forms${status ? `?status=${status}` : ''}`).then(r => r.data),
  updateSliderForm: (formId, patch) => http.put(`/admin/slider-signup-forms/${formId}`, patch).then(r => r.data),
  deleteSliderForm: (formId) => http.delete(`/admin/slider-signup-forms/${formId}`).then(r => r.data),
  moderateSliderForm: (formId, decision, note) => http.post(`/admin/slider-signup-forms/${formId}/moderate`, { decision, note }).then(r => r.data),
  listSliderSubmissions: (status) => http.get(`/admin/slider-signup-submissions${status ? `?status=${status}` : ''}`).then(r => r.data),
  moderateSliderSubmission: (subId, decision, note) => http.post(`/admin/slider-signup-submissions/${subId}/moderate`, { decision, note }).then(r => r.data),
  // Users & password management
  listUsers: (params = {}) => http.get('/admin/users', { params }).then(r => r.data),
  setUserPassword: (uid, new_password) => http.post(`/admin/users/${uid}/set-password`, { new_password }).then(r => r.data),
  hideUser: (uid) => http.post(`/admin/users/${uid}/hide`).then(r => r.data),
  unhideUser: (uid) => http.post(`/admin/users/${uid}/unhide`).then(r => r.data),
  deleteUser: (uid) => http.delete(`/admin/users/${uid}`).then(r => r.data),
  // Blackout Game admin
  blackoutStats: () => http.get('/admin/blackout/stats').then(r => r.data),
  blackoutList: (status) => http.get(`/admin/blackout/submissions${status ? `?status=${status}` : ''}`).then(r => r.data),
  blackoutReview: (id, body) => http.post(`/admin/blackout/submissions/${id}/review`, body).then(r => r.data),
  getBlackoutIntroVideo: () => http.get('/admin/blackout/intro-video').then(r => r.data),
  updateBlackoutIntroVideo: (body) => http.put('/admin/blackout/intro-video', body).then(r => r.data),
  getPlanPrices: () => http.get('/admin/plans/prices').then(r => r.data),
  setPlanPrices: (body) => http.post('/admin/plans/prices', body).then(r => r.data),
  // PSA copy + narration voice (admin update)
  updatePsa: (patch) => http.put('/admin/psa', patch).then(r => r.data),
  // Upload / remove admin-provided PSA audio (replaces TTS narration)
  uploadPsaAudio: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post('/admin/psa/upload-audio', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    }).then(r => r.data);
  },
  deletePsaAudio: () => http.delete('/admin/psa/upload-audio').then(r => r.data),
  // Fetches a voice-preview mp3 as a blob (auth-attached) and returns a blob URL
  psaVoicePreviewBlobUrl: async (voice) => {
    const r = await http.get(`/admin/psa/voice-preview/${encodeURIComponent(voice)}.mp3`, { responseType: 'blob' });
    return URL.createObjectURL(r.data);
  },
  // God-Mode CRUD: Events / Dating / News (Phase 2/3 admin bypass)
  listEvents: (q = '') => http.get('/admin/events', { params: q ? { q } : {} }).then(r => r.data),
  createEvent: (payload) => http.post('/admin/events', payload).then(r => r.data),
  updateEvent: (eid, payload) => http.put(`/admin/events/${eid}`, payload).then(r => r.data),
  deleteEvent: (eid) => http.delete(`/admin/events/${eid}`).then(r => r.data),
  listDating: (q = '') => http.get('/admin/dating', { params: q ? { q } : {} }).then(r => r.data),
  createDating: (payload) => http.post('/admin/dating', payload).then(r => r.data),
  updateDating: (pid, payload) => http.put(`/admin/dating/${pid}`, payload).then(r => r.data),
  deleteDating: (pid) => http.delete(`/admin/dating/${pid}`).then(r => r.data),
  listDatingReports: (status = 'pending') => http.get('/admin/dating/reports', { params: { status } }).then(r => r.data),
  resolveDatingReport: (id, action) => http.post(`/admin/dating/reports/${id}/resolve`, { action }).then(r => r.data),
  listDatingVerifications: (status = 'pending') => http.get('/admin/dating/verifications', { params: { status } }).then(r => r.data),
  decideDatingVerification: (userId, action, reason = null) =>
    http.post(`/admin/dating/verifications/${userId}`, { action, reason }).then(r => r.data),
  nudgePendingOwners: (cooldownDays = 7, ownerUserIds = null) =>
    http.post('/admin/business/nudge-pending', { cooldownDays, ownerUserIds }).then(r => r.data),
  reviewOwnerEvent: (eid, action) => http.post(`/admin/events/${eid}/review`, { action }).then(r => r.data),
  listNews: (q = '') => http.get('/admin/news', { params: q ? { q } : {} }).then(r => r.data),
  createNews: (payload) => http.post('/admin/news', payload).then(r => r.data),
  updateNews: (nid, payload) => http.put(`/admin/news/${nid}`, payload).then(r => r.data),
  deleteNews: (nid) => http.delete(`/admin/news/${nid}`).then(r => r.data),
  newsSources: () => http.get('/admin/news-sources').then(r => r.data),
  addNewsSource: (payload) => http.post('/admin/news-sources', payload).then(r => r.data),
  updateNewsSource: (sid, payload) => http.put(`/admin/news-sources/${sid}`, payload).then(r => r.data),
  deleteNewsSource: (sid) => http.delete(`/admin/news-sources/${sid}`).then(r => r.data),
  reorderNewsSources: (ids) => http.post('/admin/news-sources/reorder', { ids }).then(r => r.data),
  refreshNewsSources: () => http.post('/admin/news-sources/refresh').then(r => r.data),
};

// Marketing Suite (Tier 3 · Full Package)
export const marketing = {
  landing: (businessId) => http.get('/marketing/landing' + (businessId ? `?businessId=${businessId}` : '')).then(r => r.data),
  updateLanding: (id, patch) => http.patch(`/marketing/landing/${id}`, patch).then(r => r.data),
  deleteLanding: (id) => http.delete(`/marketing/landing/${id}`).then(r => r.data),
  publicLanding: (slug) => http.get(`/l/${slug}`).then(r => r.data),
  latestSurveyForLanding: (slug) => http.get(`/l/${slug}/latest-survey`).then(r => r.data),
  optIn: (slug, body) => http.post(`/l/${slug}/optin`, body).then(r => r.data),
  contacts: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return http.get('/marketing/contacts' + (qs ? `?${qs}` : '')).then(r => r.data);
  },
  deleteContact: (id) => http.delete(`/marketing/contacts/${id}`).then(r => r.data),
  exportContactsUrl: (businessId) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const qs = businessId ? `?businessId=${businessId}` : '';
    return `${base}/api/marketing/contacts/export.xlsx${qs}`;
  },
  banners: () => http.get('/marketing/homepage-banners').then(r => r.data),
  trackBannerClick: (id) => http.post(`/marketing/banner/${id}/click`).then(r => r.data).catch(() => null),
  // Slider signup form — public + owner
  sliderFormPublic: (businessId) => http.get(`/slider/signup-form/${businessId}`).then(r => r.data),
  sliderFormSubmit: (businessId, body) => http.post(`/slider/signup-form/${businessId}/submit`, body).then(r => r.data),
  sliderFormConfirm: (token) => http.get(`/slider/signup-form/confirm/${token}`).then(r => r.data),
  sliderFormOwner: () => http.get('/business/slider-signup-form').then(r => r.data),
  sliderFormOwnerUpdate: (patch) => http.put('/business/slider-signup-form', patch).then(r => r.data),
  sliderFormOwnerRegenerate: () => http.post('/business/slider-signup-form/regenerate').then(r => r.data),
  sliderFormOwnerSubmissions: () => http.get('/business/slider-signup-form/submissions').then(r => r.data),
  saveOnboarding: (body) => http.post('/me/onboarding', body).then(r => r.data),
  dismissOnboarding: () => http.post('/me/onboarding/dismiss').then(r => r.data),
  addLocation: (city, state) => http.post('/me/locations', { city, state }).then(r => r.data),
  removeLocation: (city, state) => http.delete(`/me/locations?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`).then(r => r.data),
  // Survey Builder (Wave 2)
  surveyGenerate: (businessId, description = null) => http.post('/marketing/surveys/generate', { businessId, description }).then(r => r.data),
  createSurvey: (body) => http.post('/marketing/surveys', body).then(r => r.data),
  listSurveys: (businessId) => http.get('/marketing/surveys' + (businessId ? `?businessId=${businessId}` : '')).then(r => r.data),
  getSurvey: (sid) => http.get(`/marketing/surveys/${sid}`).then(r => r.data),
  updateSurvey: (sid, patch) => http.patch(`/marketing/surveys/${sid}`, patch).then(r => r.data),
  deleteSurvey: (sid) => http.delete(`/marketing/surveys/${sid}`).then(r => r.data),
  publicSurvey: (slug) => http.get(`/s/${slug}`).then(r => r.data),
  submitSurvey: (slug, body) => http.post(`/s/${slug}/submit`, body).then(r => r.data),
  surveyResponses: (sid) => http.get(`/marketing/surveys/${sid}/responses`).then(r => r.data),
  contactResponses: (cid) => http.get(`/marketing/contacts/${cid}/responses`).then(r => r.data),
  mySurveys: () => http.get('/me/surveys').then(r => r.data),
  // Updates Bucket (Wave 2 · features 6+7)
  ownerUpdates: (businessId, limit = 50) => {
    const p = new URLSearchParams();
    if (businessId) p.set('businessId', businessId);
    if (limit) p.set('limit', String(limit));
    return http.get('/owner/updates' + (p.toString() ? `?${p}` : '')).then(r => r.data);
  },
  triggerDigestNow: () => http.post('/admin/digest/trigger-now').then(r => r.data),
  // Wave B — Campaign Scheduler
  autoGenerateCampaign: (businessId, focus = '') =>
    http.post('/marketing/campaigns/auto-generate', { businessId, focus }).then(r => r.data),
  listCampaigns: (businessId) =>
    http.get('/marketing/campaigns' + (businessId ? `?businessId=${businessId}` : '')).then(r => r.data),
  getCampaign: (cid) => http.get(`/marketing/campaigns/${cid}`).then(r => r.data),
  patchCampaign: (cid, patch) => http.patch(`/marketing/campaigns/${cid}`, patch).then(r => r.data),
  approveCampaign: (cid) => http.post(`/marketing/campaigns/${cid}/approve`).then(r => r.data),
  pauseCampaign: (cid) => http.post(`/marketing/campaigns/${cid}/pause`).then(r => r.data),
  sendCampaignNow: (cid) => http.post(`/marketing/campaigns/${cid}/send-now`).then(r => r.data),
  deleteCampaign: (cid) => http.delete(`/marketing/campaigns/${cid}`).then(r => r.data),
  adminListCampaigns: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return http.get('/admin/campaigns' + (qs ? `?${qs}` : '')).then(r => r.data);
  },
  triggerCampaignScanNow: () => http.post('/admin/campaigns/scan-now').then(r => r.data),
  // Wave C — Auto-Responder
  listAutoResponderTriggers: () => http.get('/marketing/auto-responder/triggers').then(r => r.data),
  listAutoResponderTemplates: (businessId, ownerId) => {
    const qs = new URLSearchParams(Object.entries({ businessId, ownerId }).filter(([, v]) => v)).toString();
    return http.get('/marketing/auto-responder/templates' + (qs ? `?${qs}` : '')).then(r => r.data);
  },
  putAutoResponderTemplate: (key, patch, ownerId) =>
    http.put(`/marketing/auto-responder/templates/${key}` + (ownerId ? `?ownerId=${ownerId}` : ''), patch).then(r => r.data),
  deleteAutoResponderTemplate: (key, ownerId) =>
    http.delete(`/marketing/auto-responder/templates/${key}` + (ownerId ? `?ownerId=${ownerId}` : '')).then(r => r.data),
  toggleAutoResponder: (key, enabled, ownerId) =>
    http.patch(`/marketing/auto-responder/settings/${key}` + (ownerId ? `?ownerId=${ownerId}` : ''), { enabled }).then(r => r.data),
  generateAutoResponders: (businessId) =>
    http.post('/marketing/auto-responder/generate' + (businessId ? `?businessId=${businessId}` : '')).then(r => r.data),
  // Admin auto-responder
  adminListAutoResponderTemplates: () =>
    http.get('/admin/auto-responder/templates').then(r => r.data),
  adminPutAutoResponderTemplate: (key, patch) =>
    http.put(`/admin/auto-responder/templates/${key}`, patch).then(r => r.data),
  adminToggleAutoResponder: (key, enabled) =>
    http.patch(`/admin/auto-responder/settings/${key}`, { enabled }).then(r => r.data),
  adminListAutoResponderOwners: () =>
    http.get('/admin/auto-responder/owners').then(r => r.data),
  // Member — per-trigger opt-out + deal alert delivery mode
  getMyAutoResponderPrefs: () => http.get('/me/auto-responder-prefs').then(r => r.data),
  patchMyAutoResponderPref: (key, channel, optedOut) =>
    http.patch('/me/auto-responder-prefs', { key, channel, optedOut }).then(r => r.data),
  setDealAlertMode: (mode) => http.patch('/me/deal-alert-mode', { mode }).then(r => r.data),
};

// Wave 6 — Groups + Affiliates
export const groups = {
  list: (mine = false) => http.get('/groups' + (mine ? '?mine=1' : '')).then(r => r.data),
  public: (slug) => http.get(`/groups/${slug}`).then(r => r.data),
  create: (payload) => http.post('/groups', payload).then(r => r.data),
  update: (gid, patch) => http.put(`/groups/${gid}`, patch).then(r => r.data),
  remove: (gid) => http.delete(`/groups/${gid}`).then(r => r.data),
  join: (gid) => http.post(`/groups/${gid}/join`).then(r => r.data),
  leave: (gid) => http.post(`/groups/${gid}/leave`).then(r => r.data),
  members: (gid) => http.get(`/groups/${gid}/members`).then(r => r.data),
  invite: (gid, email) => http.post(`/groups/${gid}/members/invite`, { email }).then(r => r.data),
  removeMember: (gid, uid) => http.delete(`/groups/${gid}/members/${uid}`).then(r => r.data),
  posts: (gid) => http.get(`/groups/${gid}/posts`).then(r => r.data),
  post: (gid, payload) => http.post(`/groups/${gid}/posts`, payload).then(r => r.data),
  deletePost: (gid, pid) => http.delete(`/groups/${gid}/posts/${pid}`).then(r => r.data),
  businessBadges: (bid) => http.get(`/businesses/${bid}/groups`).then(r => r.data),
  // Stripe Connect
  connectOnboarding: (returnUrl) =>
    http.post('/me/connect/onboarding', { returnUrl }).then(r => r.data),
  connectStatus: () => http.get('/me/connect/status').then(r => r.data),
  // Membership fee checkout
  checkoutMembership: (gid) =>
    http.post(`/groups/${gid}/checkout-membership`, { origin: window.location.origin }).then(r => r.data),
  verifyMembership: (gid, sessionId) =>
    http.post(`/groups/${gid}/checkout-membership/verify`, { session_id: sessionId }).then(r => r.data),
  // Organizer dashboard
  myOrganized: () => http.get('/me/groups/organized').then(r => r.data),
  myCommissions: (gid) => http.get(`/me/groups/${gid}/commissions`).then(r => r.data),
  myPayouts: (gid) => http.get(`/me/groups/${gid}/payouts`).then(r => r.data),
};

// Soft-delete helpers reused by multiple pages.
export const deletes = {
  coupon: (id) => http.delete(`/coupons/${id}`).then(r => r.data),
  loyalty: (id) => http.delete(`/loyalty-programs/${id}`).then(r => r.data),
  auction: (id) => http.delete(`/auction-items/${id}`).then(r => r.data),
  business: (id) => http.delete(`/businesses/${id}`).then(r => r.data),
};

export const trackApi = {
  visit: (payload) => http.post('/track/visit', payload).then(r => r.data),
  ping: (activity) => http.post('/track/member-ping', { activity }).then(r => r.data),
  visitorSignup: (visitorId) => http.post('/track/visitor-signup', { visitorId }).then(r => r.data),
};

export const reportsApi = {
  summary: (start, end) => http.get('/admin/reports/summary', { params: { start, end } }).then(r => r.data),
  exportXlsx: (start, end) => http.get('/admin/reports/export', { params: { start, end }, responseType: 'blob' }).then(r => r.data),
};

export const welcomeApi = {
  get: () => http.get('/welcome-messages').then(r => r.data),
  save: (cfg) => http.put('/admin/welcome-messages', { consumer: cfg.consumer, owner: cfg.owner }).then(r => r.data),
  dismiss: () => http.post('/me/welcome-dismiss').then(r => r.data),
};

// Public "Buy first, sign up after" flow — no auth required.
export const plansGuest = {
  createOrder: (pkg) => http.post('/plans/guest/create-order', { package: pkg }).then(r => r.data),
  capture: (orderId, pkg) => http.post('/plans/guest/capture', { orderId, package: pkg }).then(r => r.data),
  get: (token) => http.get(`/plans/guest/${token}`).then(r => r.data),
  claim: (purchaseToken) => http.post('/plans/guest/claim', { purchaseToken }).then(r => r.data),
};

// Tier 1 — Stripe recurring subscription checkout (semiannual + annual).
export const plansStripe = {
  checkout: (lookup_key) => http.post('/plans/stripe/checkout', {
    lookup_key,
    origin_url: window.location.origin,
  }).then(r => r.data),
  status: (session_id) => http.get(`/plans/stripe/status/${session_id}`).then(r => r.data),
};

// Public Site Editor — admin CMS for public-facing pages.
export const siteEditor = {
  publicSections: (pageKey) => http.get(`/site/pages/${pageKey}/sections`).then(r => r.data),
  pages: () => http.get('/admin/site/pages').then(r => r.data),
  list: (pageKey) => http.get(`/admin/site/pages/${pageKey}/sections`).then(r => r.data),
  create: (pageKey, payload) => http.post(`/admin/site/pages/${pageKey}/sections`, payload).then(r => r.data),
  update: (sid, payload) => http.patch(`/admin/site/sections/${sid}`, payload).then(r => r.data),
  publish: (sid) => http.post(`/admin/site/sections/${sid}/publish`).then(r => r.data),
  unpublish: (sid) => http.post(`/admin/site/sections/${sid}/unpublish`).then(r => r.data),
  remove: (sid) => http.delete(`/admin/site/sections/${sid}`).then(r => r.data),
  reorder: (pageKey, orderedIds) => http.post(`/admin/site/pages/${pageKey}/reorder`, { orderedIds }).then(r => r.data),
};

// Site-wide pop-up manager — admin CRUD + public read.
export const sitePopup = {
  public: () => http.get('/site/popup').then(r => r.data),
  get: () => http.get('/admin/site/popup').then(r => r.data),
  save: (payload) => http.put('/admin/site/popup', payload).then(r => r.data),
  reset: () => http.post('/admin/site/popup/reset').then(r => r.data),
};

// Community — public teaser + gated member wall / stats / spotlight.
export const communityApi = {
  publicStats: () => http.get('/community/public-stats').then(r => r.data),
  reserveTotal: () => http.get('/community/reserve-total').then(r => r.data),
  members: (limit = 60) => http.get(`/community/members?limit=${limit}`).then(r => r.data),
  stats: () => http.get('/community/stats').then(r => r.data),
  spotlight: () => http.get('/community/spotlight').then(r => r.data),
  toggleSpotlight: (enabled) => http.patch('/me/spotlight-optin', { enabled }).then(r => r.data),
  toggleConsumerRole: (enabled) => http.patch('/me/consumer-role', { enabled }).then(r => r.data),
  myReferral: () => http.get('/me/referral').then(r => r.data),
};

// Blackout Game — Phase 1
export const blackout = {
  myStats: () => http.get('/blackout/my-stats').then(r => r.data),
  mySubmissions: (limit = 50) => http.get(`/blackout/my-submissions?limit=${limit}`).then(r => r.data),
  leaderboard: (limit = 25) => http.get(`/blackout/leaderboard?limit=${limit}`).then(r => r.data),
  communityEarnings: () => http.get('/blackout/community-earnings').then(r => r.data),
  claimBusiness: (bizId, payload) => http.post(`/businesses/${bizId}/claim`, payload || {}).then(r => r.data),
  claimStatus: (bizId) => http.get(`/businesses/${bizId}/claim-status`).then(r => r.data),
  submitBusiness: (payload) => http.post('/blackout/submit-business', payload).then(r => r.data),
  submitEvent: (payload) => http.post('/blackout/submit-event', payload).then(r => r.data),
  introVideo: () => http.get('/blackout/intro-video').then(r => r.data),
};

const d = (p) => p.then(r => r.data);
export const newsletterApi = {
  list: () => d(http.get('/admin/newsletter/campaigns')),
  create: (body) => d(http.post('/admin/newsletter/campaigns', body)),
  update: (id, body) => d(http.put(`/admin/newsletter/campaigns/${id}`, body)),
  remove: (id) => d(http.delete(`/admin/newsletter/campaigns/${id}`)),
  preview: (id, audience) => d(http.get(`/admin/newsletter/campaigns/${id}/preview`, { params: { audience } })),
  eligible: (id) => d(http.get(`/admin/newsletter/campaigns/${id}/eligible`)),
  test: (id, audience) => d(http.post(`/admin/newsletter/campaigns/${id}/test`, { audience })),
  send: (id, audience, confirm) => d(http.post(`/admin/newsletter/campaigns/${id}/send`, { audience, confirm })),
  publish: (id) => d(http.post(`/admin/newsletter/campaigns/${id}/publish`)),
  unpublish: (id) => d(http.post(`/admin/newsletter/campaigns/${id}/unpublish`)),
  uploadFile: (file) => { const fd = new FormData(); fd.append('file', file); return d(http.post('/admin/newsletter/upload-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } })); },
  settings: () => d(http.get('/admin/newsletter/settings')),
  saveSettings: (body) => d(http.put('/admin/newsletter/settings', body)),
  contacts: (q = '') => d(http.get('/admin/newsletter/contacts', { params: { q } })),
  addContact: (body) => d(http.post('/admin/newsletter/contacts', body)),
  addBulk: (text, source) => d(http.post('/admin/newsletter/contacts/bulk', { text, source })),
  importFile: (file, source) => { const fd = new FormData(); fd.append('file', file); return d(http.post('/admin/newsletter/contacts/import', fd, { params: { source }, headers: { 'Content-Type': 'multipart/form-data' } })); },
  editContact: (id, body) => d(http.put(`/admin/newsletter/contacts/${id}`, body)),
  deleteContact: (id) => d(http.delete(`/admin/newsletter/contacts/${id}`)),
  signup: (body) => d(http.post('/newsletter/signup', body)),
  confirm: (token) => d(http.post('/newsletter/confirm', { token })),
  unsubscribe: (body) => d(http.post('/newsletter/unsubscribe', body)),
  latest: () => d(http.get('/newsletter/latest')),
  click: (cid, bid) => d(http.post(`/newsletter/click/${cid}/${bid}`)),
};

export default http;
